import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { MessageSquare, MoreVertical, Pencil, Trash2, History, Loader2, Send } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import {
  listProposalComments,
  createProposalComment,
  updateProposalComment,
  deleteProposalComment,
  type ProposalComment,
} from "@/lib/proposalsApi";

interface ProposalCommentThreadProps {
  proposalId: string;
}

const DUBAI_TIME_ZONE = "Asia/Dubai";

const formatTimestamp = (value?: string | null) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return `${parsed.toLocaleString("en-GB", {
    timeZone: DUBAI_TIME_ZONE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })} GST`;
};

const initialsFromName = (name?: string | null) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((part) => part[0]?.toUpperCase() ?? "").join("") || "?";
};

const ProposalCommentThread = ({ proposalId }: ProposalCommentThreadProps) => {
  const { user } = useAuth();
  const { toast } = useToast();

  const [comments, setComments] = useState<ProposalComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [newContent, setNewContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");
  const [savingEditId, setSavingEditId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadComments = async () => {
    try {
      setLoading(true);
      const response = await listProposalComments(proposalId);
      setComments(response.items || []);
    } catch (error: any) {
      toast({
        title: "Error loading activity",
        description: error?.message || "Failed to load proposal activity.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (proposalId) {
      loadComments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalId]);

  const handleSubmit = async () => {
    const trimmed = newContent.trim();
    if (!trimmed) return;
    try {
      setSubmitting(true);
      const created = await createProposalComment(proposalId, trimmed);
      setComments((prev) => [...prev, created]);
      setNewContent("");
    } catch (error: any) {
      toast({
        title: "Error posting message",
        description: error?.message || "Failed to post message.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const startEditing = (comment: ProposalComment) => {
    setEditingId(comment.id);
    setEditContent(comment.content);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditContent("");
  };

  const saveEdit = async (comment: ProposalComment) => {
    const trimmed = editContent.trim();
    if (!trimmed) return;
    try {
      setSavingEditId(comment.id);
      const updated = await updateProposalComment(proposalId, comment.id, trimmed);
      setComments((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      cancelEditing();
    } catch (error: any) {
      toast({
        title: "Error updating message",
        description: error?.message || "Failed to update message.",
        variant: "destructive",
      });
    } finally {
      setSavingEditId(null);
    }
  };

  const handleDelete = async (comment: ProposalComment) => {
    const confirmed = window.confirm("Delete this message? This cannot be undone.");
    if (!confirmed) return;
    try {
      setDeletingId(comment.id);
      await deleteProposalComment(proposalId, comment.id);
      setComments((prev) => prev.filter((item) => item.id !== comment.id));
    } catch (error: any) {
      toast({
        title: "Error deleting message",
        description: error?.message || "Failed to delete message.",
        variant: "destructive",
      });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Card>
      <CardHeader className="px-4 py-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <MessageSquare className="h-5 w-5" />
          Activity
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 px-4 pb-4">
        {loading ? (
          <div className="flex items-center justify-center py-6 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : comments.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">No activity yet. Be the first to add a note.</p>
        ) : (
          <div className="space-y-4">
            {comments.map((comment) => {
              const authorId = comment.author?.actor_id ?? null;
              const isOwn = !!authorId && !!user?.id && authorId === user.id;
              const isEditing = editingId === comment.id;

              return (
                <div key={comment.id} className="flex gap-3">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={undefined} alt={comment.author?.actor_name ?? "User"} />
                    <AvatarFallback className="text-xs">
                      {initialsFromName(comment.author?.actor_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">{comment.author?.actor_name ?? "Unknown"}</span>
                      <span className="text-xs text-muted-foreground">{formatTimestamp(comment.created_at)}</span>
                      {comment.edited && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground underline decoration-dotted"
                            >
                              <History className="h-3 w-3" />
                              edited
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="w-80 max-h-72 overflow-y-auto space-y-3">
                            <p className="text-xs font-medium text-muted-foreground">Previous versions</p>
                            {comment.versions.length === 0 ? (
                              <p className="text-xs text-muted-foreground">No previous versions.</p>
                            ) : (
                              comment.versions
                                .slice()
                                .reverse()
                                .map((version, index) => (
                                  <div key={index} className="border-l-2 pl-2 space-y-0.5">
                                    <p className="text-xs text-muted-foreground">{formatTimestamp(version.edited_at)}</p>
                                    <p className="text-sm whitespace-pre-wrap">{version.content}</p>
                                  </div>
                                ))
                            )}
                          </PopoverContent>
                        </Popover>
                      )}
                      {isOwn && !isEditing && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              className="ml-auto text-muted-foreground hover:text-foreground"
                              aria-label="Message options"
                            >
                              <MoreVertical className="h-4 w-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => startEditing(comment)}>
                              <Pencil className="mr-2 h-4 w-4" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDelete(comment)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>

                    {isEditing ? (
                      <div className="mt-1 space-y-2">
                        <Textarea
                          value={editContent}
                          onChange={(e) => setEditContent(e.target.value)}
                          rows={3}
                          className="text-sm"
                          autoFocus
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => saveEdit(comment)}
                            disabled={savingEditId === comment.id || !editContent.trim()}
                          >
                            {savingEditId === comment.id && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
                            Save
                          </Button>
                          <Button size="sm" variant="outline" onClick={cancelEditing}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm mt-0.5 whitespace-pre-wrap break-words">
                        {deletingId === comment.id ? (
                          <span className="text-muted-foreground italic">Deleting…</span>
                        ) : (
                          comment.content
                        )}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex gap-3 pt-2 border-t">
          <Avatar className="h-8 w-8">
            <AvatarImage src={user?.profile_photo ?? undefined} alt={user?.full_name ?? "You"} />
            <AvatarFallback className="text-xs">{initialsFromName(user?.full_name)}</AvatarFallback>
          </Avatar>
          <div className="flex-1 space-y-2">
            <Textarea
              placeholder="Add a note or update about this proposal..."
              value={newContent}
              onChange={(e) => setNewContent(e.target.value)}
              rows={2}
              className="text-sm"
            />
            <div className="flex justify-end">
              <Button size="sm" onClick={handleSubmit} disabled={submitting || !newContent.trim()}>
                {submitting ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <Send className="mr-2 h-3 w-3" />}
                Post
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProposalCommentThread;
